import { Dispatch, SetStateAction, useEffect, useRef } from "react";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { SeaDataLayersManager } from "features/shared/DataLayerManager";
import { authStore } from "entities/Auth";
import { searchFilterStore } from "entities/SearchFilter";
import { seaSavedCardStore } from "entities/SeaSavedCard";
import { createSeaSearchQueryParams, seaSearchCardStore } from "entities/SeaSearchCard";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";

import { seaSearchValueValidator } from "../utils";
import { useSeaSearch } from "./seaSearch.hook";

type UseSeaRequestStrategyReturnType = {
  handleSearch: (reference: string) => void;
};

type UseSeaRequestStrategyParams = {
  isReferenceEqualToOpenedCard: (reference: string) => boolean;
  searchValue: string;
  setErrorMessage: Dispatch<SetStateAction<string>>;
  openCard: (card: ShipmentModel) => void;
  showMainView: () => void;
  showIntroView: () => void;
  clearViewHistory: () => void;
};

export const useSeaRequestStrategy = ({
  isReferenceEqualToOpenedCard,
  searchValue,
  setErrorMessage,
  openCard,
  showMainView,
  showIntroView,
  clearViewHistory,
}: UseSeaRequestStrategyParams): UseSeaRequestStrategyReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);
  const savedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(seaSearchCardStore.selectors.searchCards);
  const carrier = useAppSelector(searchFilterStore.selectors.carrier);
  const { seaSearchRequest, getSeaCards, isSeaSearchLoading } = useSeaSearch({ isAuthenticated });

  const validatorMeta = useRef<ReturnType<typeof seaSearchValueValidator.validate>>(
    seaSearchValueValidator.validate(searchValue)
  );

  useEffect(() => {
    validatorMeta.current = seaSearchValueValidator.validate(searchValue);
  }, [searchValue]);

  const getRequestFuncByStrategy = (
    strategy: "authenticated" | "not-authenticated",
    reference: string
  ): (() => void) | null => {
    const carrierScacCode = carrier?.scacCodes[0];
    const reqParams = createSeaSearchQueryParams({
      requestType: "search",
      model: {
        reference,
        carrierScac: carrierScacCode,
      },
    });
    if (!reqParams) return null;

    switch (strategy) {
      case "authenticated":
        return () => {
          getSeaCards(
            { where: { reference } },
            {
              onSettled: (response) => {
                if (response?.length) {
                  const data = response[0];
                  dispatch(seaSavedCardStore.actions.prependSavedCardAction({ data }));
                  if (data?.reference) {
                    // scrollToElement(data.reference);
                    openCard(data);
                  }
                } else {
                  const dlm = new SeaDataLayersManager().searchInitiated();
                  seaSearchRequest(reqParams, {
                    onSuccess: (response) => {
                      if (response.requestLimitReached) return;

                      dlm.setModel(response).handleSearch();
                      dispatch(seaSearchCardStore.actions.prependSearchCardAction({ data: response }));
                      if (response?.reference) {
                        // scrollToElement(response.reference);
                        if (response.isSuccess) {
                          openCard(response);
                        }
                      }
                    },
                  });
                }
              },
            }
          );
        };
      case "not-authenticated":
      default:
        return () => {
          const dlm = new SeaDataLayersManager().searchInitiated();
          seaSearchRequest(reqParams, {
            onWebAppRequestLimitReached: () => {
              if (searchCards.length > 0) {
                showMainView();
              } else {
                showIntroView();
              }
            },
            onSuccess: (response) => {
              if (response.requestLimitReached) return;

              dlm.setModel(response).handleSearch();
              dispatch(seaSearchCardStore.actions.prependSearchCardAction({ data: response }));
              if (response?.reference) {
                // scrollToElement(response.reference);
                if (response.isSuccess) {
                  openCard(response);
                }
              }
            },
          });
        };
    }
  };

  /**
   * 1. find existing search card OR
   * 2. if not authenticated - block search OR
   * 3. find existing user card OR
   * 4. search user card in DB OR
   * 5. run search and prepend search card to search cards list
   */
  const performRequest = async (reference: string) => {
    const targetSearchCard = searchCards.find((card) => card.reference === reference);
    if (targetSearchCard) {
      if (targetSearchCard?.reference && targetSearchCard.isSuccess) {
        openCard(targetSearchCard);
      }
      return;
    }

    const targetSavedCard = savedCards.find((card) => card.reference === reference);
    if (targetSavedCard) {
      if (targetSavedCard?.reference) {
        openCard(targetSavedCard);
      }
      return;
    }

    if (isSeaSearchLoading) return;

    const requestFunc = getRequestFuncByStrategy(isAuthenticated ? "authenticated" : "not-authenticated", reference);

    requestFunc?.();
  };

  const handleSearch = (reference: string) => {
    const { isValid, errorMessage } = validatorMeta.current;

    if (isValid) {
      if (isReferenceEqualToOpenedCard(reference)) return;
      setErrorMessage("");
      performRequest(reference);
      clearViewHistory();
      return;
    }
    setErrorMessage(errorMessage);
  };

  return {
    handleSearch,
  };
};
